<template>
  <div class="fixed-header">
    <div class="bg-[#144A78] text-white shadow-md z-10">
      <!-- Top Bar with Banner -->
      <div class="bg-white relative">
        <div class="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          <img
            :src="getLocalizedImage"
            alt="Header Banner"
            class="w-full max-w-[1200px] h-auto max-h-[70px] sm:max-h-[90px] md:max-h-[120px] lg:max-h-[170px] object-contain mx-auto"
          />
        </div>
      </div>

      <!-- Navigation Bar for Desktop -->
      <div class="hidden md:block bg-[#0a192f]">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="flex justify-between items-center h-16">
            <!-- Desktop Navigation Links -->
            <nav class="flex items-center space-x-1 sm:px-10 font-large text-lg text-white-300">
              <router-link :to="getRoute('home')" class="nav-link">{{ $t('home') }}</router-link>
              <router-link :to="getGatedRoute('followup')" class="nav-link">{{ $t('followup') }}</router-link>
              <router-link :to="getRoute('contact')" class="nav-link">{{ $t('contactUs') }}</router-link>
              <a
                href="https://cirt.cm"
                target="_blank"
                rel="noopener noreferrer"
                class="nav-link-external"
              >
                {{ $t('About CIRT') }}
              </a>
            </nav>

            <!-- Desktop Right side buttons: Language Toggle + Report Button -->
            <div class="flex items-center space-x-3">
              <!-- Report Button -->
              <router-link :to="getRoute('report-incident')" class="report-button">
                {{ $t('reportIncident') }}
              </router-link>
              
              <!-- Language Toggle Button in Navbar -->
              <div class="relative">
                <button
                  @click="toggleDropdown('language')"
                  class="flex items-center space-x-1 px-3 py-1 rounded-md text-sm font-medium transition duration-200 bg-transparent hover:bg-white/10"
                >
                  <img 
                    :src="`https://flagcdn.com/w20/${currentLanguageFlag}.png`" 
                    :alt="currentLanguage" 
                    class="w-5 h-auto"
                  />
                  <span class="text-white text-xs font-medium">{{ currentLanguage }}</span>
                  <i class="fas fa-chevron-down w-2.5 h-2.5 text-white ml-1 transition-transform duration-200" :class="{'rotate-180': dropdown === 'language'}"></i>
                </button>
                
                <!-- Language Dropdown -->
                <!-- Language Dropdown -->
                <transition name="dropdown-fade">
                  <div
                    v-show="dropdown === 'language'"
                    class="absolute right-0 mt-2 w-48 rounded-md shadow-lg py-2 bg-white ring-1 ring-black ring-opacity-5 z-20 origin-top-right"
                  >
                    <button
                      @click="changeLanguage('en')"
                      class="w-full flex items-center px-4 py-3 text-base transition-colors hover:bg-gray-100"
                      :class="{'bg-blue-50 text-blue-700 font-semibold': currentLanguage === 'EN', 'text-gray-700': currentLanguage !== 'EN'}"
                    >
                      <img src="https://flagcdn.com/w20/gb.png" alt="English" class="w-6 h-auto mr-3" />
                      <span class="flex-1 text-left">English</span>
                      <svg v-if="currentLanguage === 'EN'" class="w-5 h-5 text-blue-600 ml-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                      </svg>
                    </button>
                    <button
                      @click="changeLanguage('fr')"
                      class="w-full flex items-center px-4 py-3 text-base transition-colors hover:bg-gray-100"
                      :class="{'bg-blue-50 text-blue-700 font-semibold': currentLanguage === 'FR', 'text-gray-700': currentLanguage !== 'FR'}"
                    >
                      <img src="https://flagcdn.com/w20/fr.png" alt="French" class="w-6 h-auto mr-3" />
                      <span class="flex-1 text-left">Français</span>
                      <svg v-if="currentLanguage === 'FR'" class="w-5 h-5 text-blue-600 ml-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                      </svg>
                    </button>
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Header -->
      <div class="md:hidden bg-[#0a192f]">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="flex justify-between items-center h-16">
            <!-- Mobile Menu Button - Left Side -->
            <button @click="toggleMobileMenu" class="text-white focus:outline-none p-2">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <!-- Mobile Language Toggle - Right Side -->
            <div class="relative">
              <button
                ref="langBtnMobile"
                @click.stop="toggleMobileLang"
                class="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition duration-200 bg-transparent hover:bg-white/10"
              >
                <img 
                  :src="`https://flagcdn.com/w20/${currentLanguageFlag}.png`" 
                  :alt="currentLanguage" 
                  class="w-5 h-auto"
                />
                <span class="text-white text-xs font-medium">{{ currentLanguage }}</span>
              </button>

              <!-- Teleported dropdown — renders directly in <body> to escape all stacking contexts -->
              <Teleport to="body">
                <transition name="lang-pop">
                  <div
                    v-if="mobileLangOpen"
                    ref="mobileLangDropdown"
                    class="fixed rounded-lg shadow-2xl bg-white ring-1 ring-black/10 overflow-hidden"
                    :style="mobileLangStyle"
                    @click.stop
                  >
                    <button
                      @click="changeLanguage('en')"
                      class="lang-option w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors hover:bg-gray-50"
                      :class="currentLanguage === 'EN' ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'"
                    >
                      <img src="https://flagcdn.com/w20/gb.png" alt="EN" class="w-4 h-auto rounded-sm flex-shrink-0" />
                      <span>English</span>
                      <svg v-if="currentLanguage === 'EN'" class="w-3 h-3 text-blue-600 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                      </svg>
                    </button>
                    <div class="h-px bg-gray-100 mx-2"></div>
                    <button
                      @click="changeLanguage('fr')"
                      class="lang-option w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors hover:bg-gray-50"
                      :class="currentLanguage === 'FR' ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'"
                    >
                      <img src="https://flagcdn.com/w20/fr.png" alt="FR" class="w-4 h-auto rounded-sm flex-shrink-0" />
                      <span>Français</span>
                      <svg v-if="currentLanguage === 'FR'" class="w-3 h-3 text-blue-600 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                      </svg>
                    </button>
                  </div>
                </transition>
              </Teleport>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Menu Modal -->
      <div
        v-show="isMobileMenuOpen"
        class="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden flex justify-center items-center p-4"
        @click="toggleMobileMenu"
      >
        <div
          class="bg-[#144A78] rounded-lg p-4 sm:p-6 w-full max-w-xs sm:max-w-sm relative"
          @click.stop
        >
          <!-- Close Button -->
          <button
            @click="toggleMobileMenu"
            class="absolute top-2 right-2 text-white hover:text-gray-300 focus:outline-none"
          >
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <nav class="flex flex-col space-y-3 sm:space-y-4 mt-6">
            <router-link
              :to="getRoute('home')"
              class="text-white px-3 py-2 rounded-md text-sm sm:text-base font-medium hover:bg-blue-700 transition duration-300 focus:outline-none focus:shadow-outline w-full text-left"
              @click="toggleMobileMenu"
            >
              {{ $t('home') }}
            </router-link>

            <router-link
              :to="getGatedRoute('followup')"
              class="text-white px-3 py-2 rounded-md text-sm sm:text-base font-medium hover:bg-blue-700 transition duration-300 focus:outline-none focus:shadow-outline w-full text-left"
              @click="toggleMobileMenu"
            >
              {{ $t('followup') }}
            </router-link>

            <router-link
              :to="getRoute('contact')"
              class="text-white px-3 py-2 rounded-md text-sm sm:text-base font-medium hover:bg-blue-700 transition duration-300 w-full text-left"
              @click="toggleMobileMenu"
            >
              {{ $t('contactUs') }}
            </router-link>

            <a
              href="https://cirt.cm"
              target="_blank"
              rel="noopener noreferrer"
              class="text-white px-3 py-2 rounded-md text-sm sm:text-base font-medium hover:bg-blue-700 transition duration-300 w-full text-left"
              @click="toggleMobileMenu"
            >
              {{ $t('About CIRT') }}
            </a>

            <!-- Report Incident Button -->
            <router-link
              :to="getRoute('report-incident')"
              class="bg-red-600 hover:bg-red-700 px-3 py-2 rounded-md text-sm sm:text-base font-medium transition duration-300 w-full text-center text-white"
              @click="toggleMobileMenu"
            >
              {{ $t('reportIncident') }}
            </router-link>
          </nav>
        </div>
      </div>
    </div>
    
    <!-- Bottom Stripe -->
    <div style="height: 1.5px; background-color: #95c2f0;" class="sm:h-1.5"></div>
  </div>
</template>

<script>
export default {
  name: "HeaderComponent",
  data() {
    return {
      dropdown: null,
      isMobileMenuOpen: false,
      mobileLangOpen: false,
      mobileLangStyle: {},
    };
  },
  computed: {
    currentLanguage() {
      return this.$i18n.locale.toUpperCase();
    },
    currentLanguageFlag() {
      return this.$i18n.locale === 'fr' ? 'fr' : 'gb';
    },
    getLocalizedImage() {
      const language = this.$i18n.locale || 'en';
      try {
        const isMobile = window.innerWidth < 768;
        const imageName = isMobile ? `banner-${language}` : `antic-${language}`;
        return require(`@/assets/images/${imageName}.png`);
      } catch (e) {
        return require(`@/assets/images/antic-en.png`);
      }
    },
    currentLang() {
      return this.$route.params.lang || this.$i18n.locale || 'en';
    }
  },
  methods: {
    getRoute(routeName, routeParams = {}) {
      const params = { 
        lang: this.currentLang,
        ...routeParams 
      };
      return { name: routeName, params };
    },

    getGatedRoute(routeName) {
      const typeMap = {
        'followup':       'followup',
        'cyber-incident': 'cyber',
        'antic-complaint':'complaint',
      };
      return { name: 'recaptcha-gate', params: { lang: this.currentLang, type: typeMap[routeName] || routeName } };
    },
    
    toggleMobileLang() {
      if (this.mobileLangOpen) {
        this.mobileLangOpen = false;
        return;
      }
      // Calculate position from the button's bounding rect
      const btn = this.$refs.langBtnMobile;
      if (btn) {
        const rect = btn.getBoundingClientRect();
        this.mobileLangStyle = {
          top: `${rect.bottom + 6}px`,
          right: `${window.innerWidth - rect.right}px`,
          minWidth: '130px',
          zIndex: 999999,
        };
      }
      this.mobileLangOpen = true;
    },

    toggleDropdown(dropdownName) {
      this.dropdown = this.dropdown === dropdownName ? null : dropdownName;
      if (dropdownName && this.isMobileMenuOpen) {
        this.isMobileMenuOpen = false;
      }
    },
    
    changeLanguage(lang) {
      const newLang = lang.toLowerCase();
      this.$i18n.locale = newLang;
      const currentRoute = this.$route;
      const routeName = currentRoute.name;
      
      if (routeName === 'default') {
        this.$router.push(this.getRoute('home'));
        this.dropdown = null;
        this.isMobileMenuOpen = false;
        return;
      }
      
      if (routeName && routeName !== 'default') {
        const params = { ...currentRoute.params, lang: newLang };
        const query = { ...currentRoute.query };
        
        this.$router.push({
          name: routeName,
          params,
          query
        })
      } else {
        this.$router.push(this.getRoute('home'));
      }
      
      this.dropdown = null;
      this.isMobileMenuOpen = false;
      this.mobileLangOpen = false;
    },
    
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
      if (this.isMobileMenuOpen) {
        this.dropdown = null;
      }
    },
    
    handleClickOutside(event) {
      if (!event.target.closest('.relative') && this.dropdown) {
        this.dropdown = null;
      }
      if (this.mobileLangOpen) {
        const btn = this.$refs.langBtnMobile;
        const drop = this.$refs.mobileLangDropdown;
        if (btn && !btn.contains(event.target) && drop && !drop.contains(event.target)) {
          this.mobileLangOpen = false;
        }
      }
      if (!event.target.closest('.md\\:hidden') && this.isMobileMenuOpen) {
        this.isMobileMenuOpen = false;
      }
    },
  },
  
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    
    // Sync i18n locale with router language
    const routerLang = this.$route.params.lang;
    if (routerLang && routerLang !== this.$i18n.locale) {
      this.$i18n.locale = routerLang;
    }
  },
  
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
};
</script>

<style scoped>
.fixed-header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
}

.nav-link {
  @apply px-3 py-1.5 rounded-md font-medium transition duration-200;
  color: white !important;
  text-decoration: none !important;
  font-size: 0.875rem;
  font-family: 'Roboto', sans-serif;
}

.nav-link:hover {
  background-color: #1067be !important;
}

.nav-link-external {
  @apply px-3 py-1.5 rounded-md font-medium transition duration-200;
  color: white !important;
  text-decoration: none !important;
  font-size: 0.875rem;
  font-family: 'Roboto', sans-serif;
}

.nav-link-external:hover {
  background-color: #1067be !important;
}

/* Report Button - Desktop */
.report-button {
  @apply bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-md font-medium transition duration-200 whitespace-nowrap;
  font-size: 0.875rem;
  font-family: 'Roboto', sans-serif;
}

/* Language Button - Transparent */
button[class*="language"] {
  background-color: transparent !important;
}

/* Dropdown Animation */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* Compact language popup animation */
.lang-pop-enter-active {
  transition: opacity 0.15s ease, transform 0.15s cubic-bezier(0.2, 0, 0, 1.2);
}
.lang-pop-leave-active {
  transition: opacity 0.1s ease, transform 0.1s ease;
}
.lang-pop-enter-from,
.lang-pop-leave-to {
  opacity: 0;
  transform: scale(0.92) translateY(-4px);
}

/* Ensure mobile language dropdown escapes stacking context */
.lang-dropdown-mobile {
  isolation: auto;
}

/* Smooth option hover */
.lang-option {
  transition: background-color 0.12s ease;
}

/* Rotate chevron */
.rotate-180 {
  transform: rotate(180deg);
}

.transition-transform {
  transition: transform 0.3s ease-in-out;
}

/* Hide/Show logic */
@media (max-width: 767px) {
  /* Hide desktop menu on mobile */
  .hidden.md\\:block {
    display: none !important;
  }
  
  /* Hide desktop report button on mobile */
  .report-button {
    display: none !important;
  }
  
  /* Ensure mobile menu is visible */
  .md\\:hidden {
    display: block !important;
  }
}

@media (min-width: 768px) {
  /* Hide mobile header on desktop */
  .md\\:hidden {
    display: none !important;
  }
  
  /* Show desktop elements */
  .hidden.md\\:block {
    display: flex !important;
  }
}

/* Ensure proper z-index layering */
.z-10 {
  z-index: 10;
}

.z-20 {
  z-index: 20;
}

.z-30 {
  z-index: 30;
}

.z-50 {
  z-index: 50;
}

/* Improve touch targets for mobile */
@media (max-width: 767px) {
  button, 
  .nav-link, 
  .report-button {
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}

/* Better focus states for accessibility */
button:focus,
a:focus,
.nav-link:focus,
.report-button:focus {
  outline: 2px solid rgba(255, 255, 255, 0.5);
  outline-offset: 2px;
}

/* Smooth transitions */
* {
  transition: background-color 0.3s ease, color 0.3s ease;
}

/* Image styling */
img {
  object-fit: contain;
}

/* Remove default button styling */
button {
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
}

/* Flag image styling */
img[alt*="flag"] {
  display: block;
  max-width: 100%;
  height: auto;
}

/* Mobile modal styling */
@media (max-width: 767px) {
  .bg-\[\#144A78\] {
    max-height: 90vh;
    overflow-y: auto;
  }
  
  /* Improve touch targets for mobile modal */
  nav a {
    min-height: 44px;
    display: flex;
    align-items: center;
  }
}

/* Hover Effects */
.hover\:bg-blue-700:hover {
  background-color: #1067be !important;
}

.hover\:bg-red-700:hover {
  background-color: #b91c1c !important;
}

.hover\:bg-white\\10:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

/* Mobile Menu Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fixed.inset-0 {
  animation: fadeIn 0.2s ease-in-out;
}

/* Only show one report button per device */
@media (max-width: 767px) {
  /* Hide desktop report button on mobile */
  .report-button {
    display: none !important;
  }
}

@media (min-width: 768px) {
  /* Show desktop report button */
  .report-button {
    display: inline-flex !important;
  }
}

/* Remove any default margins/paddings */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* Ensure proper text contrast */
.text-white {
  color: white !important;
}

/* Ensure links have no underline */
a {
  text-decoration: none !important;
}
</style>